'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import { useKyc } from '@/hooks/useKyc';
import { useAddresses } from '@/hooks/useAddresses';
import { Button } from '../ui/Button';
import {
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  Camera,
  Check,
  AlertCircle,
  Lock,
  ChevronRight,
  Home,
  Globe,
  Calendar,
  MessageSquare,
  ScanFace,
  UploadCloud,
  Clock,
  ShieldCheck,
  Eye,
  Loader2,
  CheckCircle2,
  Plus,
  Star,
  Trash2,
  Pencil,
  X,
  Briefcase,
  CreditCard,
  Package,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCurrency } from '@/contexts/CurrencyContext';
import { SUPPORTED_CURRENCIES, CURRENCY_CONFIG, isValidCurrency } from '@/lib/config/app.config';
import type { CustomerPreferences, AddressType, CreateAddressRequest } from '@/types/customer';

type ProfileTab = 'general' | 'preferences' | 'kyc' | 'security';

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'Français' },
  { value: 'es', label: 'Español' },
  { value: 'de', label: 'Deutsch' },
  { value: 'pt', label: 'Português' },
  { value: 'ar', label: 'العربية' },
  { value: 'zh', label: '中文' },
  { value: 'ja', label: '日本語' },
  { value: 'ru', label: 'Русский' },
  { value: 'hi', label: 'हिन्दी' },
] as const;

const TIMEZONE_OPTIONS: string[] = (() => {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return [
      'Europe/London', 'Europe/Paris', 'Europe/Berlin',
      'America/New_York', 'America/Chicago', 'America/Los_Angeles',
      'Asia/Dubai', 'Asia/Singapore', 'Asia/Tokyo',
      'Africa/Johannesburg', 'Indian/Mauritius',
      'Australia/Sydney', 'Pacific/Auckland',
    ];
  }
})();

interface ProfileFormData {
  firstName: string;
  lastName: string;
  phone: string;
  nationality: string;
  dateOfBirth: string;
  preferredContactMethod: 'email' | 'phone' | 'whatsapp' | '';
}

export const Profile: React.FC = () => {
  const { customer, isLoading, updateProfile, getCustomerClient, setNewPassword } = useCustomerAuth();
  const kyc = useKyc();
  const addressesHook = useAddresses();
  const { selectedCurrency, setSelectedCurrency } = useCurrency();
  const [activeTab, setActiveTab] = useState<ProfileTab>('general');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Form state for profile data
  const [formData, setFormData] = useState<ProfileFormData>({
    firstName: '',
    lastName: '',
    phone: '',
    nationality: '',
    dateOfBirth: '',
    preferredContactMethod: '',
  });
  
  // Track if form has unsaved changes
  const [hasChanges, setHasChanges] = useState(false);

  // Preferences state — defaults match Route Royal server-side defaults
  const [preferences, setPreferences] = useState<CustomerPreferences>({
    marketingEmailsEnabled: true,
    marketingSmsEnabled: false,
    transactionalEmailsEnabled: true,
    priceChangeAlertsEnabled: true,
    statusChangeAlertsEnabled: true,
  });
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsSuccess, setPrefsSuccess] = useState(false);
  const [prefsError, setPrefsError] = useState<string | null>(null);

  // Security state
  const [passwordForm, setPasswordForm] = useState({ current: '', newPassword: '', confirm: '' });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // KYC document upload handler — triggers file picker and uploads via signed URL
  const handleDocumentUpload = useCallback((requirementId: string) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.jpg,.jpeg,.png,.gif,.webp';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      try {
        const uploadUrlResult = await kyc.getUploadUrl(requirementId, {
          fileName: file.name,
          mimeType: file.type,
          fileSize: file.size,
        });
        if (!uploadUrlResult) return;

        // Upload file to the signed URL
        await fetch(uploadUrlResult.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        });

        // Complete the upload
        await kyc.completeUpload(requirementId, {
          documentId: uploadUrlResult.documentId,
        });

        // Refresh to get updated requirement statuses
        await kyc.refresh();
      } catch (err) {
        console.error('[Profile] Document upload failed:', err);
      }
    };
    input.click();
  }, [kyc]);

  // Initialize form data when customer loads
  useEffect(() => {
    if (customer) {
      setFormData({
        firstName: customer.firstName || '',
        lastName: customer.lastName || '',
        phone: customer.phone || '',
        nationality: customer.nationality || '',
        dateOfBirth: customer.dateOfBirth || '',
        preferredContactMethod: customer.preferredContactMethod || '',
      });
      setHasChanges(false);
    }
  }, [customer]);

  // Load preferences when the preferences tab is opened
  useEffect(() => {
    if (activeTab === 'preferences') {
      const loadPreferences = async () => {
        const client = getCustomerClient();
        if (!client) return;

        setPrefsLoading(true);
        try {
          const prefs = await client.getPreferences();

          // Auto-detect timezone if not yet set
          if (!prefs.timezone) {
            try {
              prefs.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            } catch { /* browser may not support this */ }
          }

          setPreferences(prefs);

          // Sync currency preference from Route Royal into the app-wide context
          if (prefs.currencyPreference && isValidCurrency(prefs.currencyPreference) && prefs.currencyPreference !== selectedCurrency) {
            setSelectedCurrency(prefs.currencyPreference);
          }
        } catch (err) {
          console.error('[Profile] Failed to load preferences:', err);
        } finally {
          setPrefsLoading(false);
        }
      };
      loadPreferences();
    }
  }, [activeTab, getCustomerClient]);

  // Handle input changes
  const handleInputChange = useCallback((field: keyof ProfileFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setSaveSuccess(false);
    setSaveError(null);
  }, []);

  const navItems: { id: ProfileTab; label: string; icon: React.ElementType; description: string }[] =
    [
      { id: 'general', label: 'General Profile', icon: User, description: 'Personal details' },
      { id: 'preferences', label: 'Preferences', icon: Home, description: 'Settings' },
      { id: 'kyc', label: 'Compliance & KYC', icon: FileText, description: 'Identity & documents' },
      { id: 'security', label: 'Login & Security', icon: Lock, description: 'Password & 2FA' },
    ];

  const handleSave = async (e?: React.FormEvent) => {
    // Prevent form submission from refreshing the page
    if (e) {
      e.preventDefault();
    }
    
    if (!updateProfile) {
      setSaveError('Profile updates are not available at this time.');
      return;
    }
    
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    
    try {
      const updateData: Record<string, string> = {};
      if (formData.firstName) updateData.firstName = formData.firstName;
      if (formData.lastName) updateData.lastName = formData.lastName;
      if (formData.phone) updateData.phone = formData.phone;
      if (formData.nationality) updateData.nationality = formData.nationality;
      if (formData.dateOfBirth) updateData.dateOfBirth = formData.dateOfBirth;
      if (formData.preferredContactMethod) updateData.preferredContactMethod = formData.preferredContactMethod;
      await updateProfile(updateData);
      setSaveSuccess(true);
      setHasChanges(false);
      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('[Profile] Save failed:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to save profile changes.');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle preferences save
  const handleSavePreferences = async () => {
    const client = getCustomerClient();
    if (!client) return;

    setPrefsSaving(true);
    setPrefsError(null);
    setPrefsSuccess(false);

    try {
      const updatedPrefs = await client.updatePreferences(preferences);
      setPreferences(updatedPrefs);
      setPrefsSuccess(true);
      setTimeout(() => setPrefsSuccess(false), 3000);

      // Sync currency preference into the app-wide context
      if (updatedPrefs.currencyPreference && isValidCurrency(updatedPrefs.currencyPreference) && updatedPrefs.currencyPreference !== selectedCurrency) {
        setSelectedCurrency(updatedPrefs.currencyPreference);
      }
    } catch (error) {
      console.error('[Profile] Preferences save failed:', error);
      setPrefsError(error instanceof Error ? error.message : 'Failed to save preferences.');
    } finally {
      setPrefsSaving(false);
    }
  };

  // Handle password update
  const handlePasswordUpdate = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirm) {
      setPasswordError('Passwords do not match.');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long.');
      return;
    }

    setPasswordSaving(true);
    setPasswordError(null);
    setPasswordSuccess(false);

    try {
      await setNewPassword(passwordForm.newPassword);
      setPasswordSuccess(true);
      setPasswordForm({ current: '', newPassword: '', confirm: '' });
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (error) {
      console.error('[Profile] Password update failed:', error);
      setPasswordError(error instanceof Error ? error.message : 'Failed to update password.');
    } finally {
      setPasswordSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
      </div>
    );
  }

  if (!customer) {
    return null;
  }

  return (
    <div className="animate-[fadeIn_0.3s_ease-out] max-w-6xl mx-auto pb-12 relative">
      {/* Header */}
      <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h2 className="text-3xl font-semibold text-stone-900 tracking-tight">
            Profile & Settings
          </h2>
          <p className="text-stone-500 mt-2 text-lg font-light">
            Manage your personal information and preferences.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-stone-400 uppercase tracking-wider">
            Last login: Today
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-3 lg:sticky lg:top-8 h-fit space-y-6">
          {/* Mini Profile Card */}
          <div className="bg-white p-6 rounded-3xl border border-stone-200 shadow-sm flex flex-col items-center text-center">
            <div className="relative mb-4 group cursor-pointer">
              {customer.avatarUrl ? (
                <Image
                  src={customer.avatarUrl}
                  alt="Profile"
                  width={96}
                  height={96}
                  className="w-24 h-24 rounded-full object-cover border-4 border-stone-50 group-hover:brightness-90 transition-all"
                  unoptimized
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-stone-100 border-4 border-stone-50 flex items-center justify-center">
                  <User size={40} className="text-stone-400" />
                </div>
              )}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera className="text-white drop-shadow-md" size={24} />
              </div>
              <div className="absolute bottom-0 right-0 bg-green-500 w-6 h-6 rounded-full border-4 border-white flex items-center justify-center">
                <Check size={12} className="text-white stroke-[3]" />
              </div>
            </div>
            <h3 className="font-bold text-lg text-stone-900">
              {customer.firstName || customer.lastName 
                ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim()
                : customer.displayName || 'Client'}
            </h3>
            <p className="text-stone-500 text-sm mb-4">{customer.email}</p>
            <div className="px-3 py-1 bg-stone-100 rounded-full text-xs font-medium text-stone-600 border border-stone-200 uppercase tracking-wide">
              Client
            </div>
          </div>

          {/* Nav Menu */}
          <nav className="bg-white rounded-3xl border border-stone-200 shadow-sm overflow-hidden p-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={cn(
                    'w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all duration-200',
                    activeTab === item.id
                      ? 'bg-stone-900 text-white shadow-md'
                      : 'text-stone-500 hover:bg-stone-50 hover:text-stone-900'
                  )}
                >
                  <div className={cn('p-2 rounded-lg', activeTab === item.id ? 'bg-white/10' : 'bg-stone-100')}>
                    <Icon size={18} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{item.label}</p>
                    <p className={cn('text-[10px]', activeTab === item.id ? 'text-stone-400' : 'text-stone-400')}>
                      {item.description}
                    </p>
                  </div>
                  {activeTab === item.id && <ChevronRight size={16} className="ml-auto text-stone-400" />}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-9 space-y-6">
          {/* TAB: GENERAL */}
          {activeTab === 'general' && (
            <form onSubmit={handleSave} className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
              <div className="bg-white rounded-3xl border border-stone-200 shadow-sm p-6 md:p-8">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 pb-6 border-b border-stone-100">
                  <h3 className="text-xl font-bold text-stone-900">Personal Information</h3>
                  <div className="flex items-center gap-3">
                    {saveSuccess && (
                      <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium">
                        <CheckCircle2 size={16} />
                        Saved!
                      </span>
                    )}
                    {saveError && (
                      <span className="flex items-center gap-1.5 text-sm text-red-600 font-medium">
                        <AlertCircle size={16} />
                        {saveError}
                      </span>
                    )}
                    <Button type="submit" disabled={isSaving || !hasChanges}>
                      {isSaving ? (
                        <>
                          <Loader2 size={16} className="mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InputField 
                    label="First Name" 
                    value={formData.firstName} 
                    onChange={(value) => handleInputChange('firstName', value)}
                    placeholder="Enter your first name" 
                  />
                  <InputField 
                    label="Last Name" 
                    value={formData.lastName}
                    onChange={(value) => handleInputChange('lastName', value)}
                    placeholder="Enter your last name" 
                  />
                  <InputField
                    label="Email Address"
                    value={customer.email || ''}
                    type="email"
                    icon={<Mail size={16} />}
                    disabled
                  />
                  <InputField
                    label="Phone Number"
                    value={formData.phone}
                    onChange={(value) => handleInputChange('phone', value)}
                    type="tel"
                    icon={<Phone size={16} />}
                    placeholder="Enter your phone number"
                  />
                  <InputField
                    label="Nationality"
                    value={formData.nationality}
                    onChange={(value) => handleInputChange('nationality', value)}
                    icon={<Globe size={16} />}
                    placeholder="Enter your nationality"
                  />
                  <InputField
                    label="Date of Birth"
                    value={formData.dateOfBirth}
                    onChange={(value) => handleInputChange('dateOfBirth', value)}
                    type="date"
                    icon={<Calendar size={16} />}
                    placeholder="Select your date of birth"
                  />
                  <div>
                    <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">
                      Preferred Contact Method
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">
                        <MessageSquare size={16} />
                      </div>
                      <select
                        value={formData.preferredContactMethod}
                        onChange={(e) => handleInputChange('preferredContactMethod', e.target.value)}
                        className="w-full py-2.5 pl-10 pr-4 bg-stone-50 border border-stone-200 rounded-xl text-stone-900 text-sm focus:bg-white focus:ring-2 focus:ring-stone-900 focus:border-stone-900 outline-none transition-all appearance-none"
                      >
                        <option value="">Select preferred method</option>
                        <option value="email">Email</option>
                        <option value="phone">Phone</option>
                        <option value="whatsapp">WhatsApp</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <AddressSection addressesHook={addressesHook} />
            </form>
          )}

          {/* TAB: PREFERENCES */}
          {activeTab === 'preferences' && (
            <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
              <div className="bg-white rounded-3xl border border-stone-200 shadow-sm p-6 md:p-8">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-6 border-b border-stone-100 mb-6">
                  <h3 className="text-xl font-bold text-stone-900">Preferences</h3>
                  <div className="flex items-center gap-3">
                    {prefsSuccess && (
                      <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium">
                        <CheckCircle2 size={16} />
                        Saved!
                      </span>
                    )}
                    {prefsError && (
                      <span className="flex items-center gap-1.5 text-sm text-red-600 font-medium">
                        <AlertCircle size={16} />
                        {prefsError}
                      </span>
                    )}
                    <Button onClick={handleSavePreferences} disabled={prefsSaving || prefsLoading}>
                      {prefsSaving ? (
                        <>
                          <Loader2 size={16} className="mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save Preferences'
                      )}
                    </Button>
                  </div>
                </div>

                {prefsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
                  </div>
                ) : (
                  <div className="space-y-8">
                    {/* Display & Regional */}
                    <div>
                      <label className="block text-sm font-bold text-stone-900 mb-4">
                        Display &amp; Regional
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">
                            Language
                          </label>
                          <select
                            value={preferences.languagePreference ?? ''}
                            onChange={(e) => setPreferences(prev => ({ ...prev, languagePreference: e.target.value }))}
                            className="w-full py-2.5 px-4 bg-stone-50 border border-stone-200 rounded-xl text-stone-900 text-sm focus:bg-white focus:ring-2 focus:ring-stone-900 focus:border-stone-900 outline-none transition-all appearance-none"
                          >
                            <option value="">Select language</option>
                            {LANGUAGE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">
                            Currency
                          </label>
                          <select
                            value={preferences.currencyPreference ?? ''}
                            onChange={(e) => setPreferences(prev => ({ ...prev, currencyPreference: e.target.value }))}
                            className="w-full py-2.5 px-4 bg-stone-50 border border-stone-200 rounded-xl text-stone-900 text-sm focus:bg-white focus:ring-2 focus:ring-stone-900 focus:border-stone-900 outline-none transition-all appearance-none"
                          >
                            <option value="">Select currency</option>
                            {SUPPORTED_CURRENCIES.map(code => (
                              <option key={code} value={code}>{code} — {CURRENCY_CONFIG[code].name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">
                            Timezone
                          </label>
                          <select
                            value={preferences.timezone ?? ''}
                            onChange={(e) => setPreferences(prev => ({ ...prev, timezone: e.target.value }))}
                            className="w-full py-2.5 px-4 bg-stone-50 border border-stone-200 rounded-xl text-stone-900 text-sm focus:bg-white focus:ring-2 focus:ring-stone-900 focus:border-stone-900 outline-none transition-all appearance-none"
                          >
                            <option value="">Select timezone</option>
                            {TIMEZONE_OPTIONS.map(tz => (
                              <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Communication Preferences */}
                    <div>
                      <label className="block text-sm font-bold text-stone-900 mb-4">
                        Communication Preferences
                      </label>
                      <div className="space-y-3">
                        <PreferenceCheckbox
                          label="Email notifications"
                          description="Receive transactional emails about your account activity"
                          checked={preferences.transactionalEmailsEnabled ?? true}
                          onChange={(checked) => setPreferences(prev => ({ ...prev, transactionalEmailsEnabled: checked }))}
                        />
                        <PreferenceCheckbox
                          label="SMS alerts"
                          description="Get text message alerts for important updates"
                          checked={preferences.marketingSmsEnabled ?? false}
                          onChange={(checked) => setPreferences(prev => ({ ...prev, marketingSmsEnabled: checked }))}
                        />
                        <PreferenceCheckbox
                          label="Marketing communications"
                          description="Receive promotional emails about new properties and offers"
                          checked={preferences.marketingEmailsEnabled ?? false}
                          onChange={(checked) => setPreferences(prev => ({ ...prev, marketingEmailsEnabled: checked }))}
                        />
                        <PreferenceCheckbox
                          label="Price change alerts"
                          description="Get notified when saved properties change price"
                          checked={preferences.priceChangeAlertsEnabled ?? false}
                          onChange={(checked) => setPreferences(prev => ({ ...prev, priceChangeAlertsEnabled: checked }))}
                        />
                        <PreferenceCheckbox
                          label="Status change alerts"
                          description="Get notified when saved properties change status"
                          checked={preferences.statusChangeAlertsEnabled ?? false}
                          onChange={(checked) => setPreferences(prev => ({ ...prev, statusChangeAlertsEnabled: checked }))}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB: KYC & COMPLIANCE */}
          {activeTab === 'kyc' && (
            <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
              {kyc.isLoading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
                </div>
              ) : !kyc.hasAvailablePolicy && !kyc.currentCase ? (
                /* No KYC policy configured for this organisation */
                <div className="bg-white rounded-3xl border border-stone-200 shadow-sm p-8 md:p-12 text-center">
                  <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-5">
                    <ShieldCheck size={28} className="text-stone-400" />
                  </div>
                  <h3 className="text-lg font-bold text-stone-900 mb-2">No Verification Required</h3>
                  <p className="text-stone-500 text-sm max-w-md mx-auto">
                    Identity verification is not currently required for your account. If this changes, you will be notified.
                  </p>
                </div>
              ) : (
                <>
                  {/* Overall Status Banner */}
                  <div className="bg-stone-900 rounded-3xl border border-stone-800 shadow-xl p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
                    <div className="relative">
                      <div className={cn(
                        'w-20 h-20 rounded-full border-4 flex items-center justify-center',
                        kyc.isApproved
                          ? 'bg-green-900/50 border-green-700'
                          : kyc.needsAttention
                            ? 'bg-amber-900/50 border-amber-700'
                            : 'bg-stone-800 border-stone-700'
                      )}>
                        {kyc.isApproved ? (
                          <ShieldCheck size={32} className="text-green-400" />
                        ) : kyc.needsAttention ? (
                          <AlertCircle size={32} className="text-amber-400" />
                        ) : kyc.currentCase ? (
                          <Clock size={32} className="text-stone-400" />
                        ) : (
                          <span className="text-xl font-bold text-white">—</span>
                        )}
                      </div>
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <h3 className="text-xl font-bold text-white mb-1">
                        {kyc.isApproved
                          ? 'Identity Verified'
                          : kyc.currentCase
                            ? `Verification ${kyc.getStatusLabel(kyc.currentCase.status)}`
                            : 'Identity Verification Not Started'}
                      </h3>
                      {kyc.currentCase?.caseNumber && (
                        <p className="text-stone-500 text-xs font-mono mb-1">{kyc.currentCase.caseNumber}</p>
                      )}
                      <p className="text-stone-400 text-sm max-w-lg">
                        {kyc.isApproved
                          ? 'Your identity has been verified. You have full access to all account features.'
                          : kyc.needsAttention
                            ? 'Your verification requires attention. Please review and resubmit your documents.'
                            : kyc.currentCase
                              ? 'Your documents are being reviewed. We will notify you once the review is complete.'
                              : 'Please start the verification process to enable full account features.'}
                      </p>
                      {kyc.currentCase && kyc.totalRequirements > 0 && (
                        <div className="mt-3 flex items-center gap-3">
                          <div className="flex-1 max-w-xs bg-stone-800 rounded-full h-2 overflow-hidden">
                            <div
                              className="bg-green-500 h-full rounded-full transition-all duration-500"
                              style={{ width: `${Math.round((kyc.completedRequirements / kyc.totalRequirements) * 100)}%` }}
                            />
                          </div>
                          <span className="text-stone-500 text-xs">
                            {kyc.completedRequirements} / {kyc.totalRequirements} complete
                          </span>
                        </div>
                      )}
                    </div>
                    {!kyc.currentCase && kyc.hasAvailablePolicy && (
                      <Button
                        variant="secondary"
                        className="!bg-white !text-stone-900 hover:!bg-stone-100 border-none whitespace-nowrap"
                        onClick={() => kyc.startCase()}
                        disabled={kyc.isSubmitting}
                      >
                        {kyc.isSubmitting ? (
                          <>
                            <Loader2 size={16} className="mr-2 animate-spin" />
                            Starting...
                          </>
                        ) : (
                          'Start Verification'
                        )}
                      </Button>
                    )}
                  </div>

                  {kyc.error && (
                    <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-center gap-3">
                      <AlertCircle size={18} className="text-red-500 shrink-0" />
                      <p className="text-sm text-red-700">{kyc.error}</p>
                    </div>
                  )}

                  {/* Automated Screening — only shown if check requirements exist */}
                  {kyc.checkRequirements.length > 0 && (
                    <div className="bg-white rounded-3xl border border-stone-200 shadow-sm overflow-hidden">
                      <div className="p-6 border-b border-stone-100 bg-stone-50/50 flex justify-between items-center">
                        <h3 className="text-lg font-bold text-stone-900 flex items-center gap-2">
                          <ScanFace size={20} className="text-stone-400" /> Automated Screening
                        </h3>
                        <span className="text-xs font-medium text-stone-500 uppercase tracking-wide">
                          {kyc.checkRequirements.every(r => r.status === 'VERIFIED')
                            ? 'All Passed'
                            : kyc.checkRequirements.some(r => r.status === 'REJECTED' || r.checkStatus === 'FAILED')
                              ? 'Action Required'
                              : 'In Progress'}
                        </span>
                      </div>
                      <div className={cn(
                        'grid grid-cols-1 divide-y divide-stone-100',
                        kyc.checkRequirements.length === 2 && 'md:grid-cols-2 md:divide-y-0 md:divide-x',
                        kyc.checkRequirements.length >= 3 && 'md:grid-cols-3 md:divide-y-0 md:divide-x'
                      )}>
                        {kyc.checkRequirements.map((req) => (
                          <AutomatedCheckItem
                            key={req.id}
                            label={req.checkSpec?.name ?? 'Verification Check'}
                            description={req.checkSpec?.description ?? req.checkSpec?.checkType ?? ''}
                            status={mapCheckStatus(req)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Required Documents — only shown if document requirements exist */}
                  {kyc.documentRequirements.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between px-2">
                        <h3 className="text-xl font-bold text-stone-900 flex items-center gap-2">
                          <FileText size={20} className="text-stone-400" /> Required Documents
                        </h3>
                        <span className="text-sm text-stone-500">
                          {kyc.documentRequirements.filter(r => r.status === 'VERIFIED' || r.status === 'WAIVED').length} of {kyc.documentRequirements.length} verified
                        </span>
                      </div>

                      <div className="grid grid-cols-1 gap-4">
                        {kyc.documentRequirements.map((req) => (
                          <DocumentItem
                            key={req.id}
                            title={req.documentSpec?.displayName ?? req.documentSpec?.catalogItem?.name ?? 'Document'}
                            description={req.documentSpec?.instructions ?? ''}
                            status={mapRequirementToDocStatus(req.status)}
                            {...(req.rejectionReason ? { rejectionReason: req.rejectionReason } : {})}
                            {...((req.status === 'PENDING' || req.status === 'REJECTED')
                              ? { onUpload: () => handleDocumentUpload(req.id) }
                              : {})}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Attribute Requirements — only shown if attribute requirements exist */}
                  {kyc.attributeRequirements.length > 0 && (
                    <div className="bg-white rounded-3xl border border-stone-200 shadow-sm overflow-hidden">
                      <div className="p-6 border-b border-stone-100 bg-stone-50/50">
                        <h3 className="text-lg font-bold text-stone-900 flex items-center gap-2">
                          <FileText size={20} className="text-stone-400" /> Additional Information
                        </h3>
                      </div>
                      <div className="p-6 space-y-4">
                        {kyc.attributeRequirements.map((req) => (
                          <div key={req.id} className="flex items-center justify-between p-4 rounded-xl border border-stone-200">
                            <div>
                              <h4 className="font-bold text-stone-900 text-sm">{req.attributeDef?.name ?? 'Information'}</h4>
                              {req.attributeDef?.helpText && (
                                <p className="text-xs text-stone-500 mt-0.5">{req.attributeDef.helpText}</p>
                              )}
                            </div>
                            <span className={cn(
                              'px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full',
                              req.status === 'VERIFIED' ? 'bg-green-100 text-green-700'
                                : req.status === 'SUBMITTED' || req.status === 'IN_REVIEW' ? 'bg-amber-100 text-amber-700'
                                : req.status === 'REJECTED' ? 'bg-red-100 text-red-700'
                                : 'bg-stone-200 text-stone-600'
                            )}>
                              {req.status === 'VERIFIED' ? 'Complete' : req.status === 'REJECTED' ? 'Rejected' : req.status === 'PENDING' ? 'Required' : 'Submitted'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Submit for review — shown when case is in DRAFT */}
                  {kyc.currentCase?.status === 'DRAFT' && (
                    <div className="flex items-center justify-between bg-stone-50 rounded-2xl border border-stone-200 p-5">
                      <div>
                        <p className="text-sm font-medium text-stone-900">Ready to submit?</p>
                        <p className="text-xs text-stone-500 mt-0.5">
                          {kyc.completedRequirements} of {kyc.totalRequirements} requirements complete
                        </p>
                      </div>
                      <Button
                        onClick={() => kyc.currentCase && kyc.submitCase(kyc.currentCase.id)}
                        disabled={kyc.isSubmitting}
                      >
                        {kyc.isSubmitting ? (
                          <>
                            <Loader2 size={16} className="mr-2 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          'Submit for Review'
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* TAB: SECURITY */}
          {activeTab === 'security' && (
            <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
              <div className="bg-white rounded-3xl border border-stone-200 shadow-sm p-6 md:p-8">
                <h3 className="text-xl font-bold text-stone-900 mb-6 pb-6 border-b border-stone-100 flex items-center gap-2">
                  <Lock size={20} className="text-stone-400" /> Change Password
                </h3>

                <form onSubmit={handlePasswordUpdate} className="space-y-4 max-w-md">
                  <InputField
                    label="New Password"
                    value={passwordForm.newPassword}
                    onChange={(value) => setPasswordForm(prev => ({ ...prev, newPassword: value }))}
                    type="password"
                    icon={<Lock size={16} />}
                    placeholder="Enter new password"
                  />
                  <InputField
                    label="Confirm New Password"
                    value={passwordForm.confirm}
                    onChange={(value) => setPasswordForm(prev => ({ ...prev, confirm: value }))}
                    type="password"
                    icon={<Lock size={16} />}
                    placeholder="Confirm new password"
                  />
                  <div className="flex items-center gap-3 pt-2">
                    <Button type="submit" disabled={passwordSaving || !passwordForm.newPassword || !passwordForm.confirm}>
                      {passwordSaving ? (
                        <>
                          <Loader2 size={16} className="mr-2 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        'Update Password'
                      )}
                    </Button>
                    {passwordSuccess && (
                      <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium">
                        <CheckCircle2 size={16} />
                        Password updated!
                      </span>
                    )}
                    {passwordError && (
                      <span className="flex items-center gap-1.5 text-sm text-red-600 font-medium">
                        <AlertCircle size={16} />
                        {passwordError}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-stone-400 mt-2">
                    Password must be at least 8 characters long.
                  </p>
                </form>
              </div>

              <TwoFactorSection />

              <div className="bg-white rounded-3xl border border-stone-200 shadow-sm p-6 md:p-8">
                <h3 className="text-xl font-bold text-stone-900 mb-4 text-red-600 flex items-center gap-2">
                  <AlertCircle size={20} /> Danger Zone
                </h3>
                <p className="text-stone-500 text-sm mb-6 bg-red-50 p-4 rounded-xl border border-red-100">
                  Warning: Deleting your account is irreversible. All your data will be permanently
                  removed.
                </p>
                <Button
                  variant="outline"
                  className="w-full md:w-auto border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                >
                  Delete Account
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Two-Factor Authentication Section ---

function TwoFactorSection() {
  const [mfaEnabled, setMfaEnabled] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function checkMfaStatus() {
      try {
        const { createClient } = await import('@/lib/supabase/client');
        const supabase = createClient();
        const { data } = await supabase.auth.mfa.listFactors();
        if (!cancelled) {
          const hasVerified = data?.totp?.some(
            (f: { status: string }) => f.status === 'verified'
          ) ?? false;
          setMfaEnabled(hasVerified);
        }
      } catch {
        if (!cancelled) setMfaEnabled(false);
      } finally {
        if (!cancelled) setIsChecking(false);
      }
    }
    checkMfaStatus();
    return () => { cancelled = true; };
  }, []);

  // Derive locale from window path
  const locale = typeof window !== 'undefined'
    ? (window.location.pathname.match(/^\/([a-z]{2})(\/|$)/)?.[1] || 'en')
    : 'en';

  return (
    <div className="bg-white rounded-3xl border border-stone-200 shadow-sm p-6 md:p-8">
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-4">
          <div className={cn(
            'p-3 rounded-full',
            mfaEnabled ? 'bg-emerald-50 text-emerald-600' : 'bg-stone-100 text-stone-500'
          )}>
            <ShieldCheck size={20} />
          </div>
          <div>
            <p className="font-bold text-stone-900 text-sm">
              Two-Factor Authentication (2FA)
            </p>
            <p className="text-sm text-stone-500 mt-1 max-w-xs">
              {isChecking
                ? 'Checking your 2FA status...'
                : mfaEnabled
                  ? 'Your account is protected with an authenticator app.'
                  : 'Add an extra layer of security to your account using an authenticator app.'}
            </p>
          </div>
        </div>
        {!isChecking && (
          mfaEnabled ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
              <Check size={14} />
              Enabled
            </span>
          ) : (
            <a
              href={`/${locale}/setup-2fa`}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-stone-900 text-white text-sm font-semibold hover:bg-stone-800 transition-all duration-200 shadow-sm"
            >
              Set Up
            </a>
          )
        )}
      </div>
      {!isChecking && mfaEnabled && (
        <p className="text-xs text-stone-400 mt-4 ml-16">
          To reconfigure your authenticator, use the account recovery option to reset 2FA and set it up again.
        </p>
      )}
    </div>
  );
}

// --- Address Section Component ---

const ADDRESS_TYPE_CONFIG: Record<AddressType, { label: string; icon: React.ElementType }> = {
  HOME: { label: 'Home', icon: Home },
  WORK: { label: 'Work', icon: Briefcase },
  BILLING: { label: 'Billing', icon: CreditCard },
  SHIPPING: { label: 'Shipping', icon: Package },
  OTHER: { label: 'Other', icon: MapPin },
};

const EMPTY_ADDRESS_FORM: CreateAddressRequest = {
  addressType: 'HOME',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  isPrimary: false,
  label: '',
};

import type { UseAddressesReturn } from '@/hooks/useAddresses';

const AddressSection: React.FC<{ addressesHook: UseAddressesReturn }> = ({ addressesHook }) => {
  const {
    addresses,
    isLoading: addressesLoading,
    isSaving: addressesSaving,
    error: addressError,
    createAddress,
    updateAddress,
    setPrimaryAddress,
    deleteAddress,
  } = addressesHook;

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateAddressRequest>({ ...EMPTY_ADDRESS_FORM });
  const [formSuccess, setFormSuccess] = useState(false);

  const handleFormChange = (field: keyof CreateAddressRequest, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleOpenAdd = () => {
    setEditingId(null);
    setFormData({ ...EMPTY_ADDRESS_FORM, isPrimary: addresses.length === 0 });
    setShowForm(true);
    setFormSuccess(false);
  };

  const handleOpenEdit = (addr: (typeof addresses)[0]) => {
    setEditingId(addr.id);
    setFormData({
      addressType: addr.addressType,
      addressLine1: addr.addressLine1,
      addressLine2: addr.addressLine2 || '',
      city: addr.city,
      state: addr.state || '',
      postalCode: addr.postalCode || '',
      country: addr.country,
      isPrimary: addr.isPrimary,
      label: addr.label || '',
    });
    setShowForm(true);
    setFormSuccess(false);
  };

  const handleSubmit = async () => {
    if (!formData.addressLine1 || !formData.city || !formData.country) return;

    // Strip empty optional strings — Route Royal rejects empty strings on optional fields
    const cleanedData = Object.fromEntries(
      Object.entries(formData).filter(([, v]) => v !== '' && v !== undefined)
    ) as CreateAddressRequest;

    let result;
    if (editingId) {
      result = await updateAddress(editingId, cleanedData);
    } else {
      result = await createAddress(cleanedData);
    }

    if (result) {
      setFormSuccess(true);
      setTimeout(() => {
        setShowForm(false);
        setFormData({ ...EMPTY_ADDRESS_FORM });
        setEditingId(null);
        setFormSuccess(false);
      }, 1000);
    }
  };

  const handleDelete = async (linkId: string) => {
    if (!confirm('Are you sure you want to remove this address?')) return;
    await deleteAddress(linkId);
  };

  const handleSetPrimary = async (linkId: string) => {
    await setPrimaryAddress(linkId);
  };

  return (
    <div className="bg-white rounded-3xl border border-stone-200 shadow-sm p-6 md:p-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 pb-6 border-b border-stone-100">
        <h3 className="text-xl font-bold text-stone-900">Addresses</h3>
        <Button size="sm" onClick={handleOpenAdd} disabled={addressesSaving}>
          <Plus size={14} className="mr-1.5" /> Add Address
        </Button>
      </div>

      {addressError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl flex items-center gap-2 text-sm text-red-700">
          <AlertCircle size={16} className="shrink-0" />
          {addressError}
        </div>
      )}

      {addressesLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
        </div>
      ) : addresses.length === 0 && !showForm ? (
        <div className="text-center py-8 bg-stone-50 rounded-2xl border border-dashed border-stone-200">
          <MapPin size={24} className="mx-auto text-stone-300 mb-3" />
          <p className="text-stone-500 text-sm mb-1">No addresses added yet</p>
          <p className="text-stone-400 text-xs">Add your primary address to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {addresses.map((addr) => {
            const typeConfig = ADDRESS_TYPE_CONFIG[addr.addressType] || ADDRESS_TYPE_CONFIG.OTHER;
            const TypeIcon = typeConfig.icon;
            return (
              <div
                key={addr.id}
                className={cn(
                  'flex flex-col sm:flex-row items-start gap-4 p-4 rounded-2xl border transition-all',
                  addr.isPrimary
                    ? 'border-stone-900 bg-stone-50'
                    : 'border-stone-200 hover:border-stone-300'
                )}
              >
                <div className={cn(
                  'p-2.5 rounded-xl shrink-0',
                  addr.isPrimary ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-500'
                )}>
                  <TypeIcon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-bold text-stone-900">
                      {addr.label || typeConfig.label}
                    </span>
                    {addr.isPrimary && (
                      <span className="px-2 py-0.5 bg-stone-900 text-white text-[10px] font-bold uppercase tracking-wider rounded-full">
                        Primary
                      </span>
                    )}
                    <span className="px-2 py-0.5 bg-stone-100 text-stone-500 text-[10px] font-bold uppercase tracking-wider rounded-full">
                      {typeConfig.label}
                    </span>
                  </div>
                  <p className="text-sm text-stone-600">{addr.addressLine1}</p>
                  {addr.addressLine2 && (
                    <p className="text-sm text-stone-500">{addr.addressLine2}</p>
                  )}
                  <p className="text-sm text-stone-500">
                    {[addr.city, addr.state, addr.postalCode].filter(Boolean).join(', ')}
                  </p>
                  <p className="text-sm text-stone-500">{addr.country}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {!addr.isPrimary && (
                    <button
                      onClick={() => handleSetPrimary(addr.id)}
                      disabled={addressesSaving}
                      className="p-2 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-amber-500 transition-colors"
                      title="Set as primary"
                    >
                      <Star size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => handleOpenEdit(addr)}
                    className="p-2 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-900 transition-colors"
                    title="Edit"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(addr.id)}
                    disabled={addressesSaving}
                    className="p-2 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-red-500 transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Address Form */}
      {showForm && (
        <div className="mt-6 p-5 bg-stone-50 rounded-2xl border border-stone-200 animate-[fadeIn_0.2s_ease-out]">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-bold text-stone-900">
              {editingId ? 'Edit Address' : 'New Address'}
            </h4>
            <button
              onClick={() => { setShowForm(false); setEditingId(null); }}
              className="p-1.5 rounded-lg hover:bg-stone-200 text-stone-400 hover:text-stone-900 transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Address Type */}
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">
                Address Type
              </label>
              <select
                value={formData.addressType}
                onChange={(e) => handleFormChange('addressType', e.target.value)}
                className="w-full py-2.5 px-4 bg-white border border-stone-200 rounded-xl text-stone-900 text-sm focus:ring-2 focus:ring-stone-900 focus:border-stone-900 outline-none transition-all appearance-none"
              >
                {Object.entries(ADDRESS_TYPE_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>

            {/* Label */}
            <InputField
              label="Label (Optional)"
              value={formData.label || ''}
              onChange={(value) => handleFormChange('label', value)}
              placeholder="e.g. My London Flat"
            />

            {/* Address Line 1 */}
            <div className="md:col-span-2">
              <InputField
                label="Address Line 1"
                value={formData.addressLine1}
                onChange={(value) => handleFormChange('addressLine1', value)}
                placeholder="Street address, house number"
                icon={<MapPin size={16} />}
              />
            </div>

            {/* Address Line 2 */}
            <div className="md:col-span-2">
              <InputField
                label="Address Line 2 (Optional)"
                value={formData.addressLine2 || ''}
                onChange={(value) => handleFormChange('addressLine2', value)}
                placeholder="Flat, suite, unit, building, floor"
              />
            </div>

            {/* City */}
            <InputField
              label="City / Town"
              value={formData.city}
              onChange={(value) => handleFormChange('city', value)}
              placeholder="City or town"
            />

            {/* State / County */}
            <InputField
              label="County / State"
              value={formData.state || ''}
              onChange={(value) => handleFormChange('state', value)}
              placeholder="County or state"
            />

            {/* Postal Code */}
            <InputField
              label="Postcode"
              value={formData.postalCode || ''}
              onChange={(value) => handleFormChange('postalCode', value)}
              placeholder="Postcode"
            />

            {/* Country */}
            <InputField
              label="Country"
              value={formData.country}
              onChange={(value) => handleFormChange('country', value)}
              placeholder="Country"
              icon={<Globe size={16} />}
            />
          </div>

          {/* Primary checkbox */}
          <label className="flex items-center gap-3 mt-4 p-3 bg-white border border-stone-200 rounded-xl cursor-pointer hover:border-stone-400 transition-all">
            <div className="relative flex items-center">
              <input
                type="checkbox"
                checked={formData.isPrimary || false}
                onChange={(e) => handleFormChange('isPrimary', e.target.checked)}
                className="peer appearance-none h-5 w-5 border-2 border-stone-300 rounded-md checked:bg-stone-900 checked:border-stone-900 transition-colors"
              />
              <Check
                size={12}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 peer-checked:opacity-100 pointer-events-none"
              />
            </div>
            <span className="text-sm font-medium text-stone-600">Set as primary address</span>
          </label>

          {/* Actions */}
          <div className="flex items-center gap-3 mt-4">
            <Button
              onClick={handleSubmit}
              disabled={addressesSaving || !formData.addressLine1 || !formData.city || !formData.country}
            >
              {addressesSaving ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Saving...
                </>
              ) : formSuccess ? (
                <>
                  <CheckCircle2 size={16} className="mr-2" />
                  Saved!
                </>
              ) : editingId ? (
                'Update Address'
              ) : (
                'Add Address'
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => { setShowForm(false); setEditingId(null); }}
              disabled={addressesSaving}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Helper Functions ---

/**
 * Map a KycRequirementStatus to the DocumentItem visual status
 */
function mapRequirementToDocStatus(
  status: string
): 'verified' | 'in_review' | 'missing' | 'rejected' {
  switch (status) {
    case 'VERIFIED':
    case 'WAIVED':
      return 'verified';
    case 'SUBMITTED':
    case 'IN_REVIEW':
      return 'in_review';
    case 'REJECTED':
      return 'rejected';
    default:
      return 'missing';
  }
}

/**
 * Map a KYC check requirement to the AutomatedCheckItem visual status
 */
function mapCheckStatus(
  req: { status: string; checkStatus?: string | null }
): 'passed' | 'failed' | 'in_progress' | 'pending' {
  if (req.status === 'VERIFIED' || req.checkStatus === 'PASSED') return 'passed';
  if (req.status === 'REJECTED' || req.checkStatus === 'FAILED') return 'failed';
  if (req.checkStatus === 'IN_PROGRESS' || req.status === 'IN_REVIEW') return 'in_progress';
  return 'pending';
}

// --- Helper Components ---

const PreferenceCheckbox: React.FC<{
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ label, description, checked, onChange }) => (
  <label className="flex items-start gap-3 p-3 border border-stone-200 rounded-xl cursor-pointer hover:border-stone-400 transition-all group">
    <div className="relative flex items-center mt-0.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="peer appearance-none h-5 w-5 border-2 border-stone-300 rounded-md checked:bg-stone-900 checked:border-stone-900 transition-colors"
      />
      <Check
        size={12}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 peer-checked:opacity-100 pointer-events-none"
      />
    </div>
    <div>
      <span className="text-sm font-medium text-stone-600 group-hover:text-stone-900 block">
        {label}
      </span>
      <span className="text-xs text-stone-400">{description}</span>
    </div>
  </label>
);

const InputField: React.FC<{
  label: string;
  value?: string;
  onChange?: (value: string) => void;
  type?: string;
  icon?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
}> = ({ label, value = '', onChange, type = 'text', icon, placeholder, disabled }) => (
  <div>
    <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">
      {label}
    </label>
    <div className="relative">
      {icon && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">{icon}</div>
      )}
      <input
        type={type}
        value={value}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        readOnly={!onChange && !disabled}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          'w-full py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-stone-900 text-sm focus:bg-white focus:ring-2 focus:ring-stone-900 focus:border-stone-900 outline-none transition-all',
          icon ? 'pl-10 pr-4' : 'px-4',
          disabled && 'opacity-60 cursor-not-allowed'
        )}
      />
    </div>
  </div>
);

const AutomatedCheckItem: React.FC<{
  label: string;
  description: string;
  status: 'passed' | 'failed' | 'in_progress' | 'pending';
}> = ({ label, description, status }) => {
  const config = {
    passed: { bg: 'bg-green-100 text-green-600', icon: Check, label: 'Passed', textColor: 'text-green-600' },
    failed: { bg: 'bg-red-100 text-red-600', icon: AlertCircle, label: 'Failed', textColor: 'text-red-600' },
    in_progress: { bg: 'bg-amber-100 text-amber-600', icon: Loader2, label: 'In Progress', textColor: 'text-amber-600' },
    pending: { bg: 'bg-stone-100 text-stone-400', icon: Clock, label: 'Pending', textColor: 'text-stone-400' },
  }[status];
  const Icon = config.icon;

  return (
    <div className="p-6 flex items-start gap-4">
      <div className={cn('mt-0.5 rounded-full p-1 flex items-center justify-center', config.bg)}>
        <Icon size={14} strokeWidth={3} className={status === 'in_progress' ? 'animate-spin' : ''} />
      </div>
      <div>
        <h4 className="font-bold text-stone-900 text-sm">{label}</h4>
        {description && <p className="text-xs text-stone-500 mb-1">{description}</p>}
        <span className={cn('text-[10px] font-bold uppercase tracking-wider', config.textColor)}>
          {config.label}
        </span>
      </div>
    </div>
  );
};

const DocumentItem: React.FC<{
  title: string;
  description: string;
  status: 'verified' | 'in_review' | 'missing' | 'rejected';
  rejectionReason?: string;
  onUpload?: () => void;
}> = ({ title, description, status, rejectionReason, onUpload }) => {
  const styles = {
    verified: {
      container: 'bg-white border-stone-200',
      iconBg: 'bg-green-50 text-green-600',
      icon: Check,
      badge: { bg: 'bg-green-100 text-green-700', label: 'Approved' },
    },
    in_review: {
      container: 'bg-amber-50/40 border-amber-200',
      iconBg: 'bg-amber-100 text-amber-600',
      icon: Clock,
      badge: { bg: 'bg-amber-100 text-amber-700', label: 'Pending Review' },
    },
    missing: {
      container: 'bg-stone-50 border-stone-200 border-dashed',
      iconBg: 'bg-white border border-stone-200 text-stone-400 shadow-sm',
      icon: UploadCloud,
      badge: { bg: 'bg-stone-200 text-stone-600', label: 'Required' },
    },
    rejected: {
      container: 'bg-red-50/40 border-red-200',
      iconBg: 'bg-red-100 text-red-600',
      icon: AlertCircle,
      badge: { bg: 'bg-red-100 text-red-700', label: 'Rejected' },
    },
  }[status];

  const Icon = styles.icon;

  return (
    <div
      className={cn(
        'flex flex-col md:flex-row p-5 rounded-2xl border transition-all',
        styles.container
      )}
    >
      <div className="flex-1 flex items-start gap-4 mb-4 md:mb-0">
        <div className={cn('p-3 rounded-xl shrink-0', styles.iconBg)}>
          <Icon size={20} />
        </div>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-bold text-stone-900 text-sm">{title}</h4>
            <span className={cn('px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full', styles.badge.bg)}>
              {styles.badge.label}
            </span>
          </div>
          {description && <p className="text-xs text-stone-500 max-w-sm leading-relaxed">{description}</p>}
          {rejectionReason && status === 'rejected' && (
            <p className="text-xs text-red-600 mt-1">Reason: {rejectionReason}</p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end">
        {(status === 'missing' || status === 'rejected') && onUpload && (
          <Button
            size="sm"
            variant="primary"
            className="bg-stone-900 text-white shadow-lg shadow-stone-200 w-full md:w-auto h-10 px-6"
            onClick={onUpload}
          >
            <UploadCloud size={14} className="mr-2" /> {status === 'rejected' ? 'Re-upload' : 'Upload'}
          </Button>
        )}
        {status === 'verified' && (
          <Button variant="ghost" size="sm" className="text-stone-400 hover:text-stone-900">
            <Eye size={16} className="mr-2" /> View
          </Button>
        )}
      </div>
    </div>
  );
};
